import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import './home.styles.css';
import { GreenButton } from '../../components/green-button.component';
import { BlueButton } from '../../components/blue-button.component';
import { asyncHandler } from '../../utils/async-handler';
import { useNavigate } from 'react-router-dom';
import { getUserEmailFromToken } from '../../utils/user/user';
import ModalComponent from '../../components/modal/modal.component';
import { Modal, IconButton, Link } from '@mui/material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { getUserProfile, updateUserTerms } from '../../services/userService';

const Home = () => {
	const navigate = useNavigate();
	const [userEmail, setUserEmail] = useState('');

	const formatUserName = (email: string) => {
		const namePart = email.split('@')[0];
		return namePart.charAt(0) + namePart.slice(1);
	};

	useEffect(() => {
		const notificationToken = localStorage.getItem('notificationToken');
		if (notificationToken) {
			asyncHandler.syncNotificationToken(notificationToken);
		}
	}, []);

	useEffect(() => {
		const token = localStorage.getItem('firebaseToken');
		if (token) {
			setUserEmail(getUserEmailFromToken(token));
		}
		if (!token) {
			navigate('/login');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [termsContent, setTermsContent] = useState<string>('');
	const [termsVersion, setTermsVersion] = useState<number>();

	useEffect(() => {
		axios
			.get(`${process.env.REACT_APP_BACKEND}/terms-conditions/latest`)
			.then((res) => {
				setTermsContent(res.data.content);
				setTermsVersion(res.data.id);
			})
			.catch((err) => {
				console.error('Error fetching terms:', err);
				setTermsContent('Error al cargar los términos y condiciones.');
			});
	}, []);

	const [newModal, setNewModalOpen] = useState(false);
	const [modalTermsOpen, setModalTermsOpen] = useState(false);

	const handleOpenTermsAndConditions = (e: React.MouseEvent) => {
		e.preventDefault();
		setModalTermsOpen(true);
	};
	const handleCloseTermsAndConditions = () => setModalTermsOpen(false);

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				const data = await getUserProfile();
				if (data.acceptedTermsId !== termsVersion) {
					setNewModalOpen(true);
				}
			} catch (error) {
				console.error('Error fetching user data:', error);
			}
		};

		if (termsVersion) {
			fetchUserData();
		}
	}, [termsVersion]);

	const handleAcceptNewTerms = async () => {
		try {
			if (!userEmail || !termsVersion) {
				console.error('Invalid user or Terms and Conditions');
				return;
			}

			await updateUserTerms(userEmail, termsVersion);
			setNewModalOpen(false);
		} catch (error) {
			console.error('Error accepting new Terms and Conditions:', error);
		}
	};

	const handleLogOut = async () => {
		localStorage.clear();
		navigate('/login');
	};

	const styles = {
		modalBox: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			width: { xs: '90%', sm: '80%', md: '600px' },
			bgcolor: 'background.paper',
			boxShadow: 24,
			p: { xs: 2, sm: 4 },
			borderRadius: 2,
			maxHeight: '80vh',
			overflowY: 'auto',
		},
		modalCloseButton: {
			position: 'absolute',
			top: 8,
			right: 8,
			color: 'grey.700',
		},
		modalTitle: { fontSize: { xs: '18px', sm: '22px' }, textAlign: 'center' },
		modalTextMain: { mt: 2, fontSize: { xs: '18px', sm: '20px' } },
		modalTextSecondary: { mt: 2, fontSize: { xs: '14px', sm: '16px' } },
	};

	return (
		<Box className="home-container">
			<Box className="home">
				<img
					src={'/cambiapp_logo_gray.png'}
					alt="Logo"
					width={150}
					style={{ display: 'block', margin: '0 auto' }}
				/>
				<Typography
					color={'#2e2e2e'}
					align="center"
					variant="h6"
					noWrap
					sx={{ fontWeight: 400 }}
				>
					{`Bienvenido ${formatUserName(userEmail)}`}
				</Typography>
				<Box className="buttons-container">
					<BlueButton className="button" href="/buy">
						Comprar {'\n'}
						divisas
					</BlueButton>
					<GreenButton className="button" href="/sell">
						Vender {'\n'}
						divisas
					</GreenButton>
				</Box>
				<Modal
					open={modalTermsOpen}
					onClose={handleCloseTermsAndConditions}
					aria-labelledby="terms-modal-title"
				>
					<Box sx={styles.modalBox}>
						<IconButton
							onClick={handleCloseTermsAndConditions}
							sx={styles.modalCloseButton}
						>
							✖
						</IconButton>

						<ReactMarkdown
							components={{
								h1: ({ children }) => (
									<Typography sx={styles.modalTitle}>{children}</Typography>
								),
								h2: ({ children }) => (
									<Typography
										variant="h6"
										sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}
									>
										{children}
									</Typography>
								),
								p: ({ children }) => (
									<Typography variant="body1" sx={styles.modalTextMain}>
										{children}
									</Typography>
								),
								li: ({ children }) => (
									<Typography
										component="li"
										variant="body2"
										sx={styles.modalTextSecondary}
									>
										{children}
									</Typography>
								),
								ul: ({ children }) => (
									<ul
										style={{
											paddingLeft: '1.5rem',
											marginTop: '0.5rem',
										}}
									>
										{children}
									</ul>
								),
								ol: ({ children }) => (
									<ol
										style={{
											paddingLeft: '1.5rem',
											marginTop: '0.5rem',
										}}
									>
										{children}
									</ol>
								),
							}}
						>
							{termsContent}
						</ReactMarkdown>
					</Box>
				</Modal>
				<ModalComponent
					open={newModal}
					title={'Nuevos Términos y Condiciones'}
					description={
						<>
							Para seguir operando en CambiApp debes aceptar los nuevos Términos
							y Condiciones. <br />
							<br />
							<Link
								onClick={handleOpenTermsAndConditions}
								target="_blank"
								rel="noopener noreferrer"
							>
								Leer Términos y Condiciones
							</Link>
						</>
					}
					imageSrc="/cambiapp_logo.png"
					buttonText="Aceptar"
					onButtonClick={handleAcceptNewTerms}
					onClose={handleLogOut}
					secondaryButtonText="Cancelar"
					onSecondaryClick={handleLogOut}
				/>
			</Box>
		</Box>
	);
};

export default Home;
